// unmanaged
// "Which project is this for?" on upload and on run launch
// (docs/project-tracking.md). Two forms, one field:
// - Customers: their company's project tag. Tags belong to the user's
//   company: colleagues share them, other companies never see them. Typing a
//   new name starts a new project. Renders nothing when the user's company
//   has no projects set up.
// - The team (Admin, InternalUser): any company's open project, by ID or
//   name. Empty means the Metalytics catch-all; it can be moved later.
import React, { useCallback, useEffect, useState } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useAuthStore } from "../store/authStore";
import { isTeam } from "../utils/roles";
import { fetchDashboard, fetchTags } from "./projectApi";
import { formatTime } from "./projectParts";

export const CATCH_ALL_LABEL = "Metalytics · not in a project";

export function useProjectTags() {
  const user = useAuthStore((s) => s.user);
  const staff = isTeam(user);
  const [state, setState] = useState({ available: false, tags: [], projects: [], loaded: false });
  const reload = useCallback(async () => {
    try {
      if (staff) {
        const res = await fetchDashboard();
        setState({ available: true, tags: [], projects: res?.records || [], loaded: true });
      } else {
        const res = await fetchTags();
        setState({ available: !!res?.metadata?.available, tags: res?.records || [], projects: [], loaded: true });
      }
    } catch {
      setState({ available: false, tags: [], projects: [], loaded: true });
    }
  }, [staff]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { ...state, staff, reload };
}

/** The upload/run body fields for a choice: a staff member's project ID (null
 *  = the catch-all), or a customer's tag. */
export function projectPayload(projectTags, value) {
  if (projectTags?.staff) return { project_id: value || null };
  return value ? { project_tag: value } : {};
}

/** The value a dialog starts with for an existing item (an input's project). */
export function projectValueOf(projectTags, record) {
  return (projectTags?.staff ? record?.project_id : record?.project_tag) || null;
}

export const projectLabel = (p) => `${p.id} · ${p.name}${p.company_name ? ` · ${p.company_name}` : ""}`;

export function ProjectPicker({ value, onChange, projects, label = "Which project is this for?", helperText }) {
  const selected = (projects || []).find((p) => p.id === value) || null;
  return (
    <Autocomplete
      options={projects || []}
      value={selected}
      onChange={(_, p) => onChange(p ? p.id : null)}
      getOptionLabel={projectLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(options, { inputValue }) => {
        const q = inputValue.trim().toLowerCase();
        return q ? options.filter((p) => projectLabel(p).toLowerCase().includes(q)) : options;
      }}
      renderOption={(props, p) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.id} · {p.name}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{p.company_name}</Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search by ID or name, e.g. BMS-001"
          helperText={helperText ?? `Leave empty for ${CATCH_ALL_LABEL}; you can move it later.`}
        />
      )}
    />
  );
}

export default function ProjectTagField({ value, onChange, tags, available, staff, projects, staffHelperText }) {
  const [input, setInput] = useState(value || "");
  useEffect(() => setInput(value || ""), [value]);
  if (staff) {
    return (
      <Box sx={{ mt: 2 }}>
        <ProjectPicker value={value} onChange={onChange} projects={projects} helperText={staffHelperText} />
      </Box>
    );
  }
  if (!available) return null;

  const open = (tags || []).filter((t) => t.open);
  const typed = input.trim();
  const isNew = typed && !open.some((t) => t.name.toLowerCase() === typed.toLowerCase());

  return (
    <Box sx={{ mt: 2 }}>
      <Autocomplete
        freeSolo
        options={open.map((t) => t.name)}
        value={value || null}
        inputValue={input}
        onInputChange={(_, v) => {
          setInput(v);
          onChange(v.trim() || null);
        }}
        onChange={(_, v) => onChange((v || "").trim() || null)}
        renderOption={(props, name) => {
          const tag = open.find((t) => t.name === name);
          const { key, ...rest } = props;
          return (
            <li key={key} {...rest}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>#{name}</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {tag?.status}{tag?.last_used_at ? ` · last used ${formatTime(tag.last_used_at)} by ${tag.last_used_by}` : ""}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Which project is this for? (optional)"
            placeholder="Pick one of your company's projects, or name a new one"
            helperText={isNew
              ? `"${typed}" will start a new project.`
              : "Tags are shared with your colleagues and private to your company."}
          />
        )}
      />
    </Box>
  );
}
